/**
 * Integration, Operations & Settings API
 */
import type { ItemResponse, ListResponse, DeleteResponse } from '../../types/api.js';
import type { Integration, IntegrationTestResult, BrandSettings } from '../../types/models.js';
import type { VerifyReport } from '../../types/capabilityMatrix.js';
import { apiRequest } from './core.js';

/**
 * GET /integrations/shopify/sync-status. Counts exclude soft-deleted rows;
 * `lastSyncAt` moves when a full sync finishes (ISO string, null before the
 * first). No Shopify row at all answers `{ connected: false, counts }` only.
 */
export interface ShopifySyncStatus {
  connected: boolean;
  integrationId?: string;
  lastSyncAt?: string | null;
  counts: { products: number; orders: number; customers: number };
}

export const integrationsApi = {
  list: () => apiRequest<ListResponse<Integration>>('/integrations'),
  get: (id: string) => apiRequest<ItemResponse<Integration>>(`/integrations/${id}`),
  create: (data: { name: string; type: string; credentials?: Record<string, unknown>; config?: Record<string, unknown> }) =>
    apiRequest<ItemResponse<Integration>>('/integrations', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<Integration>) => apiRequest<ItemResponse<Integration>>(`/integrations/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  disconnect: (id: string) => apiRequest<ItemResponse<Integration>>(`/integrations/${id}/disconnect`, { method: 'POST' }),
  delete: (id: string) => apiRequest<DeleteResponse>(`/integrations/${id}`, { method: 'DELETE' }),
  sync: (id: string) => apiRequest<ItemResponse<Integration>>(`/integrations/${id}/sync`, { method: 'POST' }),
  // P0 2026-08-10: sync-all now enqueues a worker job and returns immediately
  // (queued-style ack) instead of blocking until all three syncs finish.
  syncAllShopify: (id: string) => apiRequest<{ message: string; status: string; jobId: string }>(`/integrations/shopify/${id}/sync-all`, { method: 'POST' }),
  /** The synced counts and last sync time the Shopify settings page shows (and polls after a re-sync). */
  shopifySyncStatus: () => apiRequest<ItemResponse<ShopifySyncStatus>>('/integrations/shopify/sync-status'),
  shopifyInstallAuth: (shop: string) =>
    apiRequest<ItemResponse<{ authUrl: string; message: string }>>(
      `/integrations/shopify/install-auth?shop=${encodeURIComponent(shop)}`
    ),
  // Legacy App Store listing hop (cookie intent). In-app Connect uses
  // shopifyInstallAuth above for store-specific OAuth instead.
  shopifyConnectIntent: () =>
    apiRequest<ItemResponse<{ startUrl: string | null; target: 'listing' | 'pending' }>>(
      '/integrations/shopify/connect-intent',
      { method: 'POST' }
    ),
  shopifyFinalize: (shop: string) =>
    apiRequest<ItemResponse<{ integrationId: string; message: string }>>(
      '/integrations/shopify/finalize',
      { method: 'POST', body: JSON.stringify({ shop }) }
    ),
  test: (id: string) => apiRequest<ItemResponse<IntegrationTestResult>>(`/integrations/${id}/test`, { method: 'POST' }),
  /** The stored capability matrix — what every "Connected" badge should be read against. */
  verifyMatrix: () => apiRequest<ItemResponse<VerifyReport>>('/integrations/verify'),
  /** Run every check now (one platform when given) and return the fresh matrix. */
  runVerify: (platform?: string) =>
    apiRequest<ItemResponse<VerifyReport>>(
      `/integrations/verify${platform ? `?platform=${encodeURIComponent(platform)}` : ''}`,
      { method: 'POST' }
    )
};

// Dead code removed (2026-08-02 audit): the emailDomainApi module (and its
// local DnsRecord/EmailDomainSettings types) had ZERO consumers — the
// EmailDomainSettings page calls the /email-domain endpoints directly via
// apiClient with its own types (pages/settings/EmailDomainSettings/types.ts).

export const settingsApi = {
  getBrand: () => apiRequest<ItemResponse<BrandSettings>>('/settings/brand'),
  updateBrand: (data: { logoUrl?: string; faviconUrl?: string; primaryColor?: string; secondaryColor?: string; fontFamily?: string; brandVoice?: string; websiteUrl?: string; onboardingCompleted?: boolean }) =>
    apiRequest<ItemResponse<BrandSettings>>('/settings/brand', { method: 'PUT', body: JSON.stringify(data) })
};
